import { z } from 'zod';

/**
 * Desktop updates (`GET /api/desktop/update`). Crowdex Cloud serves them to the accounts linked to
 * it; a local server relays its user's request over their cloud link. The answer is
 * electron-updater's update info, or null when the app already runs the newest release.
 */

export const desktopUpdatePlatformSchema = z.enum(['darwin', 'linux', 'win32']);
export const desktopUpdateChannelSchema = z.enum(['stable', 'prerelease']);

export const desktopUpdateQuerySchema = z.object({
  platform: desktopUpdatePlatformSchema,
  /** The version the app runs. */
  version: z.string().regex(/^\d+\.\d+\.\d+$/, 'Expected a version such as 26.10.0.'),
  channel: desktopUpdateChannelSchema.default('stable'),
});

export const desktopUpdateManifestSchema = z.object({
  version: z.string(),
  releaseDate: z.string(),
  releaseNotes: z.string(),
  path: z.string(),
  sha512: z.string(),
  /** Signed download URLs: they expire within minutes, so the app asks right before downloading. */
  files: z.array(z.object({ url: z.url(), sha512: z.string(), size: z.number().optional() })),
});

export type DesktopUpdatePlatform = z.infer<typeof desktopUpdatePlatformSchema>;
export type DesktopUpdateChannel = z.infer<typeof desktopUpdateChannelSchema>;
export type DesktopUpdateQuery = z.infer<typeof desktopUpdateQuerySchema>;
export type DesktopUpdateManifest = z.infer<typeof desktopUpdateManifestSchema>;

/**
 * Desktop installers for a first install (`GET /api/desktop/download`): without `target` the
 * installers of each channel, with it a redirect to that installer's signed download URL.
 */
export const desktopInstallerTargetSchema = z.enum([
  'macos-arm64',
  'macos-x64',
  'windows-x64',
  'linux-appimage',
  'linux-deb',
]);

export const desktopDownloadQuerySchema = z.object({
  target: desktopInstallerTargetSchema.optional(),
  channel: desktopUpdateChannelSchema.default('stable'),
});

export const desktopDownloadChannelSchema = z.object({
  version: z.string(),
  publishedAt: z.string().nullable(),
  installers: z.array(
    z.object({ target: desktopInstallerTargetSchema, filename: z.string(), size: z.number() }),
  ),
});

/** Each channel's newest release with installers; null when it has none to offer. */
export const desktopDownloadsSchema = z.object({
  stable: desktopDownloadChannelSchema.nullable(),
  prerelease: desktopDownloadChannelSchema.nullable(),
});

export type DesktopInstallerTarget = z.infer<typeof desktopInstallerTargetSchema>;
export type DesktopDownloadChannel = z.infer<typeof desktopDownloadChannelSchema>;
export type DesktopDownloads = z.infer<typeof desktopDownloadsSchema>;
